// ProviderBilling.tsx — what the providers actually billed, beside what we metered.
//
// Reads GET /api/platform/usage/provider-billing, a sub-route inside
// platformAdmin.js which therefore inherits hostGate + operatorOnly
// structurally. This component adds no auth of its own and must not imply any.
//
// SCOPED TO `PT`. No import from theme.ts.
//
// WHY THIS SECTION EXISTS SEPARATELY FROM THE TABLES ABOVE IT. Everything else
// on this screen is OUR arithmetic: tokens we recorded, multiplied by rates an
// operator entered. This is the providers' own invoice. They are different
// kinds of number and adding them, or putting them in one column, would present
// a derived figure as an authoritative one.
//
// The comparison is only meaningful HERE. On the product's own admin screen our
// meter is scoped by RLS to a single tenant while the provider bills the whole
// account, so a difference between them means nothing. In this console the
// meter is summed across every tenant, so a gap is real information: either
// spend on that account from outside this platform, or a call path we are not
// recording.
//
// EMPTY IS NOT ZERO, the same rule as the rest of this screen. A provider with
// no admin credential renders as "needs a key", never as $0.00.

import React, { useCallback, useEffect, useState } from "react";
import { apiCallEx } from "../../apiClient";
import { PT, PT_NUM, fmtCost, fmtNum, fmtTokens } from "../../platformTheme";
// Reused rather than reimplemented. The provider returns its per-model
// breakdown as flat "model, direction" strings; there must be exactly one
// implementation of taking those apart, or the two screens can disagree about
// what a model cost (CLAUDE.md §7 item 4).
import { groupCostByModel } from "../admin/spendFormat";

interface Metered {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  cost: number | null;
  currency: string;
  unpriced_calls: number;
  is_partial: boolean;
  first_seen: string | null;
}
interface ProviderRow {
  provider: string;
  display_name: string;
  status: string;
  message?: string;
  credential_note?: string;
  note?: string | null;
  cost?: { amount?: number | null; currency?: string } | null;
  currency?: string;
  cost_by_model?: { model: string; amount?: number | null; currency?: string }[];
  metered?: Metered | null;
}
interface OurModel {
  provider: string; model: string; calls: number;
  input_tokens: number; output_tokens: number;
  cost: number; unpriced_calls: number; failures: number; cost_is_partial: boolean;
}
interface Payload {
  by_model?: OurModel[];
  window: { start: string; end: string; days: number };
  providers: ProviderRow[];
  meter_scope: "all_tenants" | "single_customer";
  meter_partial: boolean;
  tenants_failed: { tenant_id: string | null; tenant_name: string; error: string }[];
}

const card: React.CSSProperties = {
  background: PT.bg2,
  border: `1px solid ${PT.brd}`,
  borderRadius: PT.radiusLg,
  padding: 0,
};
const th: React.CSSProperties = {
  textAlign: "left", fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
  textTransform: "uppercase", color: PT.txD, padding: "8px 10px",
  borderBottom: `1px solid ${PT.brd}`, whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  fontSize: 12.5, padding: "9px 10px",
  borderBottom: `1px solid ${PT.brdSubtle}`, whiteSpace: "nowrap",
};

const amountOf = (c: ProviderRow["cost"]): number | null => {
  const n = c && typeof c.amount === "number" ? c.amount : NaN;
  return Number.isFinite(n) ? n : null;
};

const Caret: React.FC<{ open: boolean }> = ({ open }) => (
  <span style={{
    display: "inline-block", width: 12, color: PT.txD,
    transform: open ? "rotate(90deg)" : "none", transition: "transform .12s",
  }}>&#9656;</span>
);

const ProviderBilling: React.FC<{ days: number }> = ({ days }) => {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // OUR per-model totals, which need no provider credential. Fetched from the
  // requests route rather than recomputed here: one arithmetic, two screens.
  const [ourModels, setOurModels] = useState<OurModel[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    // One request. The per-model breakdown now rides the billing response,
    // aggregated in the database. It was briefly fetched from the requests
    // route with limit=1, which computed the whole breakdown from a SINGLE
    // call and reported one model per provider — wrong, and it looked fine.
    const r = await apiCallEx(`/platform/usage/provider-billing?days=${days}`);
    if (r.ok && r.data) {
      setData(r.data as Payload);
      setOurModels((r.data as { by_model?: OurModel[] }).by_model || []);
      setErr(null);
    } else {
      setOurModels(null);
      // An error must not render as an empty table on a spend screen.
      setData(null);
      setErr((r.data as { error?: string })?.error || `Could not read provider billing (HTTP ${r.status}).`);
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div style={{ ...card, overflowX: "auto" }}>
      <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${PT.brd}` }}>
        AI usage and costs · what the providers billed · {days}d
        <span style={{ fontWeight: 400, color: PT.txM, marginLeft: 8 }}>
          each provider's own invoice, beside our meter summed across every customer
        </span>
      </div>

      {loading && !data && (
        <div style={{ padding: 16, fontSize: 12.5, color: PT.txM }}>Asking the providers…</div>
      )}
      {err && <div style={{ padding: 16, fontSize: 12.5, color: PT.err }}>{err}</div>}

      {data && (
        <>
          {/* The scope of the meter side, said out loud. Without it the reader
              cannot know whether "our meter" means one customer or all of them,
              and the whole comparison turns on that. */}
          <div style={{ padding: "10px 16px", fontSize: 11.5, color: PT.txM, borderBottom: `1px solid ${PT.brdSubtle}` }}>
            Covering {data.window.start} to {data.window.end}. The providers' figures are account-wide.
            Our meter here is summed across {data.meter_scope === "all_tenants" ? "every customer" : "this deployment"}.
            {data.meter_partial && (
              <span style={{ color: PT.warn }}>
                {" "}Our side is INCOMPLETE: {data.tenants_failed.length} customer
                {data.tenants_failed.length === 1 ? "" : "s"} could not be read
                ({data.tenants_failed.map((t) => t.tenant_name).join(", ")}), so the metered total is a floor.
              </span>
            )}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Provider</th>
                <th style={{ ...th, textAlign: "right" }}>Our meter</th>
                <th style={{ ...th, textAlign: "right" }}>Calls</th>
                <th style={{ ...th, textAlign: "right" }}>Tokens in / out</th>
                <th style={{ ...th, textAlign: "right" }}>Provider billed</th>
                <th style={th}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => {
                const m = p.metered;
                const billed = amountOf(p.cost);
                const { models, kinds } = groupCostByModel(p.cost_by_model);
                const mine = (ourModels || []).filter((x) => x.provider === p.provider);
                const canExpand = models.length > 0 || mine.length > 0;
                const isOpen = !!open[p.provider];
                const ccy = p.cost?.currency || p.currency || m?.currency || "USD";
                // Only compare when both figures cover the same period. Our
                // records begin when this platform started metering; a provider
                // bill reaching further back is a longer measuring tape, not a
                // discrepancy.
                const since = m?.first_seen ? new Date(m.first_seen) : null;
                const shortMeasure = !!since && since.getTime() > new Date(data.window.start).getTime();
                let diff: React.ReactNode = <span style={{ color: PT.txD }}>—</span>;
                if (billed !== null && m && m.cost !== null) {
                  const d = billed - m.cost;
                  const caveat = shortMeasure && since
                    ? ` (our records begin ${since.toLocaleDateString()}, so the periods differ)`
                    : "";
                  if (Math.abs(d) < 0.005) diff = <span style={{ color: PT.txM }}>matches our meter{caveat}</span>;
                  else if (d > 0) {
                    diff = (
                      <span style={{ color: shortMeasure ? PT.txM : PT.warn }}>
                        {fmtCost(d, ccy)} more than we metered{caveat}
                        {!shortMeasure && " — spend on this account we did not record"}
                      </span>
                    );
                  } else {
                    diff = (
                      <span style={{ color: PT.txM }}>
                        {fmtCost(-d, ccy)} less than we metered{caveat}
                      </span>
                    );
                  }
                } else if (p.status === "not_configured") {
                  diff = <span style={{ color: PT.warn }}>needs an account admin key — not zero</span>;
                } else if (p.status === "metered_only") {
                  diff = <span style={{ color: PT.txM }}>this provider publishes no bill to fetch</span>;
                } else if (p.status === "error") {
                  diff = <span style={{ color: PT.err }}>{p.message}</span>;
                }

                return (
                  <React.Fragment key={p.provider}>
                    <tr>
                      <td style={{ ...td, fontWeight: 600 }}>
                        {canExpand ? (
                          <span
                            onClick={() => setOpen({ ...open, [p.provider]: !isOpen })}
                            style={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <Caret open={isOpen} /> {p.display_name}
                            <span style={{ fontWeight: 400, color: PT.txD, marginLeft: 6 }}>
                              {(models.length || mine.length)} model{(models.length || mine.length) === 1 ? "" : "s"}
                            </span>
                          </span>
                        ) : p.display_name}
                      </td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>
                        {m && m.cost !== null ? fmtCost(m.cost, m.currency) : "—"}
                        {m?.is_partial && <span style={{ color: PT.warn }}> +?</span>}
                      </td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{m ? fmtNum(m.calls) : "—"}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>
                        {m ? `${fmtTokens(m.input_tokens)} / ${fmtTokens(m.output_tokens)}` : "—"}
                      </td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>
                        {billed !== null ? fmtCost(billed, ccy)
                          : <span style={{ color: PT.txD }}>—</span>}
                      </td>
                      <td style={{ ...td, whiteSpace: "normal", fontSize: 11.5 }}>{diff}</td>
                    </tr>

                    {/* A caveat the adapter attached to an otherwise good
                        figure: the period it really covered, a token report
                        that failed, a total that is only a floor. */}
                    {p.note && (
                      <tr>
                        <td style={{ ...td, whiteSpace: "normal", color: PT.warn, fontSize: 11.5 }} colSpan={6}>
                          {p.note}
                        </td>
                      </tr>
                    )}

                    {isOpen && mine.length > 0 && (
                      <tr>
                        <td style={{ ...td, padding: 0 }} colSpan={6}>
                          {/* OUR breakdown. Present on every provider, because
                              it is arithmetic over records we already hold. The
                              provider's own per-model invoice needs an account
                              admin key, and on an estate holding none this is
                              the only answer to "which model spent it". */}
                          <div style={{ overflowX: "auto", background: PT.bg }}>
                            <div style={{ padding: "8px 10px 4px 34px", fontSize: 11, color: PT.txM }}>
                              By our meter · every customer · needs no provider key
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead>
                                <tr>
                                  <th style={{ ...th, paddingLeft: 34 }}>Model</th>
                                  <th style={{ ...th, textAlign: "right" }}>Calls</th>
                                  <th style={{ ...th, textAlign: "right" }}>Failures</th>
                                  <th style={{ ...th, textAlign: "right" }}>Tokens in</th>
                                  <th style={{ ...th, textAlign: "right" }}>Tokens out</th>
                                  <th style={{ ...th, textAlign: "right" }}>Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mine.map((mm) => (
                                  <tr key={mm.model}>
                                    <td style={{ ...td, paddingLeft: 34 }}>{mm.model}</td>
                                    <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{fmtNum(mm.calls)}</td>
                                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: mm.failures ? PT.err : PT.txD }}>
                                      {mm.failures ? fmtNum(mm.failures) : "—"}
                                    </td>
                                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(mm.input_tokens)}</td>
                                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(mm.output_tokens)}</td>
                                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>
                                      {fmtCost(mm.cost, ccy)}
                                      {mm.cost_is_partial && (
                                        <span style={{ color: PT.warn }} title={`${mm.unpriced_calls} call(s) have no price on file`}> +?</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}

                    {isOpen && models.length > 0 && (
                      <tr>
                        <td style={{ ...td, padding: 0 }} colSpan={6}>
                          {/* One row per model with a column per billing
                              category, most expensive first. The provider sends
                              these as flat "model, direction" strings; rendered
                              literally that is a wall of near-identical lines. */}
                          <div style={{ overflowX: "auto", background: PT.bg }}>
                            <div style={{ padding: "8px 10px 4px 34px", fontSize: 11, color: PT.txM }}>
                              As the provider itemised it on their invoice
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead>
                                <tr>
                                  <th style={{ ...th, paddingLeft: 34 }}>Model</th>
                                  {kinds.map((k) => (
                                    <th key={k} style={{ ...th, textAlign: "right" }}>{k}</th>
                                  ))}
                                  <th style={{ ...th, textAlign: "right" }}>Model total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {models.map((mm) => (
                                  <tr key={mm.model}>
                                    <td style={{ ...td, paddingLeft: 34 }}>{mm.model}</td>
                                    {kinds.map((k) => (
                                      <td key={k} style={{ ...td, ...PT_NUM, textAlign: "right", color: mm.kinds[k] ? undefined : PT.txD }}>
                                        {mm.kinds[k] ? fmtCost(mm.kinds[k], ccy) : "—"}
                                      </td>
                                    ))}
                                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>
                                      {fmtCost(mm.total, ccy)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <div style={{ padding: "10px 16px", fontSize: 11.5, color: PT.txM, borderTop: `1px solid ${PT.brdSubtle}` }}>
            Our meter counts calls made through AI Routing. Other parts of the product call a provider directly —
            document search turns text into numbers before it can search, and the Negotiation tab carries its own
            model chooser — and those calls reach the provider's invoice without reaching this table. That is the
            usual reason the provider's figure is the larger of the two.
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderBilling;
